import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, createAuthHeader } from '../../../config/api.config';
import { colors } from '../../../themes/typography';

// The planogram's "picked" green
const PICKED = '#15803d';

/**
 * The products worth putting in some lanes of a machine, expected gain first (this machine's own sales, then the
 * sector's), for `outgoing` = "productId:lanes,…", asked again only when it changes. `candidates` is null while
 * loading.
 */
export const useLaneCandidates = (clientId, machineId, outgoing, enabled = true) => {
  const [candidates, setCandidates] = useState(null);
  const [originLaneCapacities, setOriginLaneCapacities] = useState([]);
  useEffect(() => {
    if (!enabled || !outgoing) return undefined;
    let cancelled = false;
    setCandidates(null);
    axios.get(`${API_BASE_URL}/experiments/lane-candidates?clientId=${encodeURIComponent(clientId)}&storeId=${machineId}&outgoing=${outgoing}`,
      { headers: createAuthHeader(localStorage.getItem('access_token')) })
      .then(({ data }) => {
        if (cancelled) return;
        setCandidates(data.candidates || []);
        setOriginLaneCapacities(data.originLaneCapacities || []);
      })
      .catch(() => { if (!cancelled) setCandidates([]); });
    return () => { cancelled = true; };
  }, [enabled, outgoing, clientId, machineId]);
  return { candidates, originLaneCapacities };
};

/**
 * Those products and what they would add, under a chip per category · subcategory, each with the units its lanes load
 * against the lanes it would take (`originName`'s). `chosenId` is marked; a click calls `onPick(candidate)`.
 */
const LaneCandidateList = ({ candidates, originLaneCapacities, originName, chosenId, disabled, onPick }) => {
  const { t } = useTranslation();

  // In the order they come (the outgoing one's first)
  const groups = useMemo(() => {
    const byKey = new Map();
    (candidates || []).forEach((c) => {
      const label = [c.category, c.subcategory].filter(Boolean).join(' · ');
      if (!byKey.has(label)) byKey.set(label, { label, items: [] });
      byKey.get(label).items.push(c);
    });
    return [...byKey.values()];
  }, [candidates]);

  return (
    <>
      {candidates === null && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
          <CircularProgress size={11} />
          <Typography sx={{ fontSize: '11px', color: colors.text.secondary }}>{t('exp_candidatesLoading')}</Typography>
        </Box>
      )}
      {candidates?.length > 0 && originLaneCapacities.length > 0 && (
        <Typography sx={{ fontSize: '10.5px', color: colors.text.secondary, mb: 0.5 }}>
          {t('exp_originLoad', { name: originName, load: originLaneCapacities.join('/') })}
        </Typography>
      )}
      {groups.map((g) => (
        <Box key={g.label} sx={{ mb: 0.75 }}>
          <Box sx={{
            display: 'inline-block', px: 0.75, py: '1px', mb: 0.25, borderRadius: '10px', fontSize: '10px',
            color: colors.text.secondary, backgroundColor: colors.background.subtle, border: `1px solid ${colors.border.light}`,
          }}>
            {g.label}
          </Box>
          {g.items.map((c) => {
            const chosen = Number(chosenId) === c.productId;
            const eur = Math.round(c.expectedNetEur);
            return (
              <Box
                key={c.productId}
                component="button"
                type="button"
                disabled={disabled}
                onClick={() => onPick(c)}
                title={c.source === 'machine'
                  ? t('exp_candidateTipMachine', { eur })
                  : t('exp_candidateTipSector', { eur, count: c.machines })}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1, width: '100%', px: 0.75, py: '3px',
                  border: `1px solid ${chosen ? PICKED : 'transparent'}`, borderRadius: '6px',
                  backgroundColor: chosen ? `${PICKED}12` : 'transparent', cursor: 'pointer', textAlign: 'left',
                  '&:hover': { backgroundColor: `${PICKED}0d` },
                }}
              >
                <Typography noWrap sx={{ flex: 1, minWidth: 0, fontSize: '11.5px', color: colors.text.primary }}>{c.name}</Typography>
                {/* Units its lanes load, against the lanes it would take */}
                {c.laneCapacity != null && (
                  <Box
                    component="span"
                    title={c.sameLaneSize === false
                      ? t('exp_laneSizeOther', { count: c.laneCapacity, origin: originLaneCapacities.join('/'), name: originName })
                      : t('exp_laneSizeSame', { count: c.laneCapacity, name: originName })}
                    sx={{
                      flexShrink: 0, px: '5px', borderRadius: '8px', fontSize: '10px', lineHeight: '15px',
                      border: `1px solid ${c.sameLaneSize === false ? '#d97706' : colors.border.light}`,
                      color: c.sameLaneSize === false ? '#b45309' : colors.text.secondary,
                    }}
                  >
                    {t('exp_unitsPerLane', { count: c.laneCapacity })}
                  </Box>
                )}
                <Typography sx={{ fontSize: '11.5px', fontWeight: 600, color: PICKED, flexShrink: 0 }}>
                  {c.expectedGainPct != null ? `+${Math.round(c.expectedGainPct * 100)}%` : `+${eur} €`}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ))}
    </>
  );
};

export default LaneCandidateList;
